import { FC, useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
} from '@mui/material';
import {
  Person,
  School,
  Badge,
  AccountBalance,
  Edit,
  ExpandMore,
  Description,
} from '@mui/icons-material';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useStudentDetailsProfileHook } from '../../../../../../apis/students/studentDetails/useStudentDetailsService';
import { GENDER, TUserDetails } from '../../../../../../apis/users/users.interfaces';
import { IBankDetails } from '../../../../../../apis/staff/staffPayout.interfaces';
import StructuredModal from '../../../../../components/structuredModal/structuredModal';
import ControlledTextField from '../../../../../components/controlledComponents/ControlledTextField.component';
import useGuardianProfileService from '../../../../../../apis/guardian/sub-apis/useGuardianProfileService';
import { useNavigate } from 'react-router-dom';
import { pages } from 'src/utils/common';

interface StudentProfileTabProps {
  studentId: string;
}

const StudentProfileTab: FC<StudentProfileTabProps> = ({ studentId }) => {
  const { translateText } = useTranslation();
  const navigate = useNavigate();
  const { updateBankDetails } = useGuardianProfileService();
  const queryClient = useQueryClient();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const { data, isLoading, error } = useStudentDetailsProfileHook(studentId);

  const bankDetailsSchema = z.object({
    accountNumber: z.string().min(
      1,
      translateText('students.studentDetails.profile.validation.accountNumberRequired')
    ),
    ifscCode: z.string().min(
      1,
      translateText('students.studentDetails.profile.validation.ifscCodeRequired')
    ),
    bankName: z.string().min(
      1,
      translateText('students.studentDetails.profile.validation.bankNameRequired')
    ),
    branchName: z.string().optional(),
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<IBankDetails>({
    resolver: zodResolver(bankDetailsSchema),
    defaultValues: {
      accountNumber: '',
      ifscCode: '',
      bankName: '',
      branchName: '',
    },
  });

  const updateBankDetailsMutation = useMutation({
    mutationFn: (bankDetails: IBankDetails) => updateBankDetails(studentId, bankDetails),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['student-details-profile', studentId],
      });
      setIsEditDialogOpen(false);
    },
  });

  const handleEditClick = () => {
    if (data?.student.bankDetails) {
      reset(data.student.bankDetails);
    }
    setIsEditDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsEditDialogOpen(false);
    reset();
  };

  const onSubmit = (formData: IBankDetails) => {
    updateBankDetailsMutation.mutate(formData);
  };

  if (!studentId) {
    return (
      <Alert severity="info" data-testid="student-profile-tab-no-student">
        {translateText('students.studentDetails.profile.noStudentId')}
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="40vh"
        data-testid="student-profile-tab-loading"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Alert severity="error" data-testid="student-profile-tab-error">
        {translateText('common.error')}
      </Alert>
    );
  }

  const { student, guardians, extraGuardians } = data;
  const studentName = `${student.basicDetails.firstName} ${student.basicDetails.middleName || ''} ${student.basicDetails.lastName}`.trim();
  const admissionDate = student.admissionDate?.timestamp
    ? new Date(student.admissionDate.timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
    : translateText('common.nA');

  const getAcademicYear = (admissionTimestamp?: number): string => {
    if (!admissionTimestamp) return translateText('common.nA');
    const admissionDate = new Date(admissionTimestamp * 1000);
    const year = admissionDate.getFullYear();
    const month = admissionDate.getMonth();
    if (month >= 3) {
      return `${year}-${year + 1}`;
    } else {
      return `${year - 1}-${year}`;
    }
  };

  const academicYear = getAcademicYear(student.admissionDate?.timestamp);

  // dateOfBirth comes through as a `YYYY-MM-DD` string; parse the parts directly
  // to avoid the UTC-midnight off-by-one that `new Date('YYYY-MM-DD')` causes.
  const formatDateOfBirth = (dob?: string): string => {
    if (!dob) return translateText('common.nA');
    const [year, month, day] = dob.split('-').map(Number);
    if (!year || !month || !day) return translateText('common.nA');
    return new Date(year, month - 1, day).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatPhone = (phone: { countryCode: string; phoneNumber: string }) => {
    return `${phone.countryCode} ${phone.phoneNumber}`;
  };

  const formatAddress = (address?: {
    address1: string;
    address2?: string | null;
    city: string;
    state?: string | null;
    country?: string | null;
    postalCode: string;
  }) => {
    if (!address) return translateText('common.nA');
    const parts = [
      address.address1,
      address.address2,
      address.city,
      address.state,
      address.country,
      address.postalCode,
    ].filter(Boolean);
    return parts.join(', ');
  };

  // Show at most two guardians: the first available father (first male) and
  // the first available mother (first female), across the full guardian pool.
  const allGuardians: TUserDetails[] = [...guardians, ...extraGuardians];
  const father = allGuardians.find(
    (g) => g.basicDetails.gender === GENDER.MALE
  );
  const mother = allGuardians.find(
    (g) => g.basicDetails.gender === GENDER.FEMALE
  );
  const displayedGuardians = [
    father && {
      guardian: father,
      relationship: translateText('students.studentDetails.profile.father'),
    },
    mother && {
      guardian: mother,
      relationship: translateText('students.studentDetails.profile.mother'),
    },
  ].filter(
    (entry): entry is { guardian: TUserDetails; relationship: string } =>
      Boolean(entry)
  );

  return (
    <Grid
      container
      spacing={{ xs: 1, sm: 2, md: 3 }}
      sx={{ p: { xs: 1, sm: 2 } }}
      data-testid="student-profile-tab-container"
    >
      {/* Student Profile Section */}
      <Grid item xs={12} data-testid="student-profile-student-profile-section">
        <Card>
          <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <School color="primary" />
              <Typography
                variant="h6"
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                {translateText('students.studentDetails.profile.studentProfile')} (
                <Box component="span" data-testid="student-profile-student-id-value">
                  {student.id}
                </Box>
                )
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={{ xs: 1, sm: 2, md: 3 }}>
              <Grid item xs={12} md={3}>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap={2}
                >
                  <Avatar sx={{ width: { xs: 80, sm: 120 }, height: { xs: 80, sm: 120 } }}>
                    <Person sx={{ fontSize: { xs: 40, sm: 60 } }} />
                  </Avatar>
                  <Typography
                    variant="h6"
                    textAlign="center"
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    data-testid="student-profile-student-name-value"
                  >
                    {studentName}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={9}>
                <Grid container spacing={{ xs: 1, sm: 2 }}>
                  {/* <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      {translateText('students.studentDetails.profile.studentId')}
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {student.id}
                    </Typography>
                  </Grid> */}
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      {translateText('students.studentDetails.profile.grade')}
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      data-testid="student-profile-grade-value"
                    >
                      {student.grade || translateText('common.nA')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      {translateText('students.studentDetails.profile.section')}
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      data-testid="student-profile-section-value"
                    >
                      {student.section || translateText('common.nA')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      {translateText('students.studentDetails.profile.gender')}
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      data-testid="student-profile-gender-value"
                    >
                      {student.basicDetails.gender === GENDER.MALE
                        ? translateText('common.male')
                        : student.basicDetails.gender === GENDER.FEMALE
                          ? translateText('common.female')
                          : translateText('common.other')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} data-testid="student-profile-date-of-birth">
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      {translateText('students.studentDetails.profile.dateOfBirth')}
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      data-testid="student-profile-date-of-birth-value"
                    >
                      {formatDateOfBirth(student.basicDetails.dateOfBirth)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      {translateText('students.studentDetails.profile.email')}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      data-testid="student-profile-email-value"
                    >
                      {student.contactDetails.email?.email || translateText('common.nA')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      {translateText('students.studentDetails.profile.phone')}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      data-testid="student-profile-phone-value"
                    >
                      {student.contactDetails.phone
                        ? formatPhone(student.contactDetails.phone)
                        : translateText('common.nA')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      {translateText('students.studentDetails.profile.address')}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      data-testid="student-profile-address-value"
                    >
                      {formatAddress(student.contactDetails.address || undefined)}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Admission Details Section */}
      <Grid item xs={12} data-testid="student-profile-admission-details">
        <Card>
          <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Badge color="primary" />
              <Typography
                variant="h6"
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                {translateText('students.studentDetails.profile.admissionDetails')}
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={{ xs: 1, sm: 2 }}>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  {translateText('students.studentDetails.profile.admissionNumber')}
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={600}
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  data-testid="student-profile-admission-number-value"
                >
                  {student.id || translateText('common.nA')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  {translateText('students.studentDetails.profile.admissionDate')}
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={600}
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  data-testid="student-profile-admission-date-value"
                >
                  {admissionDate}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  {translateText('students.studentDetails.profile.academicYear')}
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={600}
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  data-testid="student-profile-academic-year-value"
                >
                  {academicYear}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  {translateText('students.studentDetails.profile.category')}
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={600}
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  data-testid="student-profile-category-value"
                >
                  {student.category || translateText('common.nA')}
                </Typography>
              </Grid>
            </Grid>

            {/* Enrollment History - Collapsible */}
            <Accordion
              disableGutters
              sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
              data-testid="student-profile-enrollment-history-accordion"
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{ px: 0 }}
                data-testid="student-profile-enrollment-history-toggle"
              >
                <Typography variant="subtitle2" fontWeight={600}>
                  {translateText('students.studentDetails.profile.enrollmentHistory')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, px: 0 }}>
                {student.enrollmentHistory?.length ? (
                  <Grid container spacing={1}>
                    {student.enrollmentHistory.map((entry, idx) => (
                      <Grid item xs={12} key={idx} data-testid={`student-profile-enrollment-entry-${idx}`}>
                        <Typography variant="body2" data-testid={`student-profile-enrollment-entry-value-${idx}`}>
                          {entry.academicYear}
                          {entry.grade && ` • ${translateText('students.studentDetails.profile.grade')} ${entry.grade}`}
                          {entry.section && ` • ${translateText('students.studentDetails.profile.section')} ${entry.section}`}
                          {entry.instituteName && ` • ${entry.instituteName}`}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary" data-testid="student-profile-enrollment-history-empty">
                    {translateText('common.nA')}
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>

            {/* Previous School Card */}
            {student.previousSchool && (
              <Box mt={2} data-testid="student-profile-previous-school">
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  {translateText('students.studentDetails.profile.previousSchool')}
                </Typography>
                <Typography variant="body1" fontWeight={600} data-testid="student-profile-previous-school-name-value">
                  {student.previousSchool.name}
                </Typography>
                {student.previousSchool.address && (
                  <Typography variant="body2" color="text.secondary" data-testid="student-profile-previous-school-address-value">
                    {student.previousSchool.address}
                  </Typography>
                )}
                {(student.previousSchool.lastGrade || student.previousSchool.lastAcademicYear) && (
                  <Typography variant="body2" color="text.secondary" data-testid="student-profile-previous-school-grade-value">
                    {[student.previousSchool.lastGrade, student.previousSchool.lastAcademicYear]
                      .filter(Boolean)
                      .join(' • ')}
                  </Typography>
                )}
              </Box>
            )}

            {/* Link to Documents tab */}
            <Box mt={2}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Description />}
                onClick={() =>
                  navigate(`${pages.students.path}/${studentId}/details/documents`)
                }
                data-testid="student-profile-related-documents-link"
              >
                {translateText('students.studentDetails.profile.relatedDocuments')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Bank Details Section */}
      <Grid item xs={12} data-testid="student-profile-bank-details-section">
        <Card>
          <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={3}
            >
              <Box display="flex" alignItems="center" gap={2}>
                <AccountBalance color="primary" />
                <Typography
                  variant="h6"
                  sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                >
                  {translateText('students.studentDetails.profile.bankDetails')}
                </Typography>
              </Box>
              {student.bankDetails && (
                <IconButton
                  onClick={handleEditClick}
                  color="primary"
                  size="small"
                  data-testid="student-profile-bank-details-edit-button"
                >
                  <Edit />
                </IconButton>
              )}
            </Box>
            <Divider sx={{ mb: 3 }} />
            {student.bankDetails ? (
              <Grid container spacing={{ xs: 1, sm: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    {translateText('students.studentDetails.profile.accountNumber')}
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    data-testid="student-profile-account-number-value"
                  >
                    {student.bankDetails.accountNumber || translateText('common.nA')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    {translateText('students.studentDetails.profile.ifscCode')}
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    data-testid="student-profile-ifsc-code-value"
                  >
                    {student.bankDetails.ifscCode || translateText('common.nA')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    {translateText('students.studentDetails.profile.bankName')}
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    data-testid="student-profile-bank-name-value"
                  >
                    {student.bankDetails.bankName || translateText('common.nA')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    {translateText('students.studentDetails.profile.branchName')}
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    data-testid="student-profile-branch-name-value"
                  >
                    {student.bankDetails.branchName || translateText('common.nA')}
                  </Typography>
                </Grid>
              </Grid>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                data-testid="student-profile-no-bank-details"
              >
                {translateText('students.studentDetails.profile.noBankDetails')}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Edit Bank Details Dialog */}
      <StructuredModal
        isOpen={isEditDialogOpen}
        onClose={handleCloseDialog}
        title={translateText('students.studentDetails.profile.editBankDetails')}
        footerPositiveAction={handleSubmit(onSubmit)}
        footerPositiveActionText={translateText('common.save')}
        footerNegativeAction={handleCloseDialog}
        footerNegativeActionText={translateText('common.cancel')}
        footerMessage={
          updateBankDetailsMutation.isError ? (
            <Typography color="error" variant="body2">
              {translateText('students.studentDetails.profile.updateError')}
            </Typography>
          ) : null
        }
        isLoading={updateBankDetailsMutation.isPending}
      >
        <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
          <Grid container spacing={2} sx={{ pt: 2 }}>
            <Grid item xs={12}>
              <ControlledTextField
                control={control}
                name="accountNumber"
                id="account-number"
                label={translateText('students.studentDetails.profile.accountNumber')}
                errorMessage={errors.accountNumber?.message}
                isGridItem={false}
                data-testid="bank-details-account-number"
              />
            </Grid>
            <Grid item xs={12}>
              <ControlledTextField
                control={control}
                name="ifscCode"
                id="ifsc-code"
                label={translateText('students.studentDetails.profile.ifscCode')}
                errorMessage={errors.ifscCode?.message}
                isGridItem={false}
                data-testid="bank-details-ifsc-code"
              />
            </Grid>
            <Grid item xs={12}>
              <ControlledTextField
                control={control}
                name="bankName"
                id="bank-name"
                label={translateText('students.studentDetails.profile.bankName')}
                errorMessage={errors.bankName?.message}
                isGridItem={false}
                data-testid="bank-details-bank-name"
              />
            </Grid>
            <Grid item xs={12}>
              <ControlledTextField
                control={control}
                name="branchName"
                id="branch-name"
                label={translateText('students.studentDetails.profile.branchName')}
                errorMessage={errors.branchName?.message}
                isGridItem={false}
                data-testid="bank-details-branch-name"
              />
            </Grid>
          </Grid>
        </form>
      </StructuredModal>

      {/* Guardian Profile Section */}
      <Grid item xs={12} data-testid="student-profile-guardian-profile-section">
        <Card>
          <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Person color="primary" />
              <Typography
                variant="h6"
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                {translateText('students.studentDetails.profile.guardianProfile')}
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            {displayedGuardians.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                data-testid="student-profile-no-guardians"
              >
                {translateText('students.studentDetails.profile.noGuardians')}
              </Typography>
            ) : (
              <Grid container spacing={{ xs: 1, sm: 2, md: 3 }}>
                {displayedGuardians.map(({ guardian, relationship }, guardianIndex) => {
                  const guardianName = `${guardian.basicDetails.firstName} ${guardian.basicDetails.middleName || ''} ${guardian.basicDetails.lastName}`.trim();

                  return (
                    <Grid item xs={12} md={6} key={guardianIndex + guardian.id} data-testid={`student-profile-guardian-card-${guardianIndex}`}>
                      <Card variant="outlined">
                        <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                          <Box display="flex" alignItems="center" gap={2} mb={2}>
                            <Avatar>
                              <Person />
                            </Avatar>
                            <Box>
                              <Typography
                                variant="subtitle1"
                                fontWeight={600}
                                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                data-testid={`student-profile-guardian-name-value-${guardianIndex}`}
                              >
                                {guardianName}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                data-testid={`student-profile-guardian-relationship-value-${guardianIndex}`}
                              >
                                {relationship}
                              </Typography>
                            </Box>
                          </Box>
                          <Grid container spacing={1}>
                            <Grid item xs={12}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                              >
                                {translateText('students.studentDetails.profile.email')}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                data-testid={`student-profile-guardian-email-value-${guardianIndex}`}
                              >
                                {guardian.contactDetails.email?.email || translateText('common.nA')}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                              >
                                {translateText('students.studentDetails.profile.phone')}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                data-testid={`student-profile-guardian-phone-value-${guardianIndex}`}
                              >
                                {guardian.contactDetails.phone
                                  ? formatPhone(guardian.contactDetails.phone)
                                  : translateText('common.nA')}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                              >
                                {translateText('students.studentDetails.profile.address')}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                data-testid={`student-profile-guardian-address-value-${guardianIndex}`}
                              >
                                {formatAddress(guardian.contactDetails.address || undefined)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default StudentProfileTab;
